// Billing client: the public plan catalog + wrappers around the billing
// callables in functions/billing/index.js. Every payment goes through a
// server-created Stripe Checkout session tied to the signed-in user (no
// payment-link fallbacks: those could take money without crediting the account).
import { doc, getDoc, onSnapshot } from 'firebase/firestore';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { getApp } from 'firebase/app';
import { db } from '../firebaseConfig';
import { DEFAULT_PUBLIC_CATALOG, planList } from '../utils/billingPlans';
import {
  stripeUiTierFromHostname,
  effectiveStripeBillingMode,
} from '../utils/stripeBillingMode';
import { track } from '../utils/analytics';

// ── billing mode ──────────────────────────────────────────────────────────────

/** Read site_config/stripe_settings and return { tier, mode, settings }. */
export async function getStripeBillingContext() {
  let settings = {};
  try {
    const snap = await getDoc(doc(db, 'site_config', 'stripe_settings'));
    if (snap.exists()) settings = snap.data() || {};
  } catch (e) {
    console.warn('[stripeService] stripe_settings read failed:', e?.message);
  }
  const hostname = typeof window !== 'undefined' ? window.location.hostname : '';
  const tier = stripeUiTierFromHostname(hostname);
  const mode = effectiveStripeBillingMode(tier, {
    useLiveInDevelopment: settings.useLiveInDevelopment === true,
    useLiveInStaging: settings.useLiveInStaging === true,
  });
  return { tier, mode, settings };
}

export async function getStripeBillingMode() {
  const { mode } = await getStripeBillingContext();
  return mode;
}

// ── catalog ───────────────────────────────────────────────────────────────────

/** Live public catalog (site_config/plans_public); calls back with defaults first. */
export function subscribeToPlanCatalog(onCatalog) {
  onCatalog(DEFAULT_PUBLIC_CATALOG, { source: 'defaults' });
  try {
    return onSnapshot(
      doc(db, 'site_config', 'plans_public'),
      (snap) => { if (snap.exists()) onCatalog(snap.data(), { source: 'firestore' }); },
      (err) => console.warn('[stripeService] plans_public listen failed:', err?.message),
    );
  } catch (e) {
    console.warn('[stripeService] plans_public subscribe failed:', e?.message);
    return () => {};
  }
}

/** Ordered plans (used by Community member tiers). */
export const getPlans = async () => {
  let catalog = DEFAULT_PUBLIC_CATALOG;
  try {
    const snap = await getDoc(doc(db, 'site_config', 'plans_public'));
    if (snap.exists()) catalog = snap.data();
  } catch { /* defaults */ }
  return planList(catalog).map((pl) => ({ key: pl.key.toLowerCase(), name: pl.name, price: pl.prices?.month?.amount || 0, interval: 'month' }));
};

// ── callables ─────────────────────────────────────────────────────────────────

const call = async (name, data = {}) => {
  try {
    const res = await httpsCallable(getFunctions(getApp()), name)(data);
    return res.data;
  } catch (e) {
    // Firebase wraps HttpsError messages; surface the server's sentence.
    const msg = e?.message?.replace(/^(FirebaseError:\s*)?/, '') || 'Something went wrong.';
    const err = new Error(msg);
    err.code = e?.code;
    throw err;
  }
};

const origin = () => (typeof window !== 'undefined' && window.location?.origin) || 'https://betoptimized.com';

/** Redirect to Stripe Checkout. `mode: 'test'` is honored only for admins/testers. */
export async function startCheckout({ planKey, interval = 'month', mode } = {}) {
  track('checkout_start', { plan: planKey, interval, test: mode === 'test' });
  const data = await call('createCheckoutSession', { planKey, interval, mode, origin: origin() });
  if (!data?.url) throw new Error('Checkout did not return a URL.');
  if (typeof window !== 'undefined') window.location.assign(data.url);
  return data;
}

/** Apply a finished checkout right away (no waiting on the webhook). */
export const confirmCheckout = (sessionId) => call('confirmCheckoutSession', { sessionId });

export const previewPlanChange = (planKey, interval) => call('previewPlanChange', { planKey, interval });
export const changePlan = (planKey, interval, prorationDate) => {
  track('plan_change', { plan: planKey, interval });
  return call('changeSubscriptionPlan', { planKey, interval, prorationDate });
};
export const cancelSubscription = () => call('cancelSubscription');
export const resumeSubscription = () => call('resumeSubscription');

/**
 * Open the Stripe billing portal in this tab (a window.open after an await is
 * blocked as a popup by most browsers).
 */
export async function openBillingPortal(returnUrl) {
  const data = await call('createBillingPortalSession', { returnUrl: returnUrl || `${origin()}/plans` });
  if (data?.url && typeof window !== 'undefined') window.location.assign(data.url);
  return data;
}

// Admin
export const setupStripeCatalog = ({ mode, apply = false } = {}) => call('adminSetupStripeCatalog', { mode, apply });

/** @deprecated kept so old imports don't crash; use startCheckout. */
export const handleSubscription = (planKey) => startCheckout({ planKey });
